import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { v4 as uuid } from 'uuid';
import { ref, uploadBytes } from 'firebase/storage';
import { doc, setDoc } from 'firebase/firestore';
import { useSnackbar } from 'notistack';
import { storage, db } from '../../firebase-config';
import { getUser } from '../api/user-api/UserAPI';
import { VendorStatementDoc } from './types';

interface VendorStatementsUploadModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Upload vendor statement PDF and initialize a job for it
 * @param file - The file to initialize a job for
 * @returns
 */
const initializeJob = async (file: File) => {
  try {
    const fileId = uuid();
    const fileName = `${fileId}.pdf`;
    const storageRef = ref(storage, `vendor-statements/${fileName}`);
    await uploadBytes(storageRef, file);

    // Create Firestore document to initialize the job
    const user = getUser();
    const currentTime = new Date().toISOString();

    const vendorStatementDoc: VendorStatementDoc = {
      id: fileId,
      createdBy: user?.uid || 'unknown',
      createdAt: currentTime,
      completedAt: null,
      completedBy: null,
      status: 'awaiting-ai',
      data: null,
      deletedAt: null,
      deletedBy: null,
    };

    const docRef = doc(db, 'vendor-statements', fileId);
    await setDoc(docRef, vendorStatementDoc);
    return { success: true, fileName: file.name, fileId };
  } catch (error) {
    // Upload failed for file: file.name
    return { success: false, fileName: file.name, error };
  }
};

/**
 * Modal for uploading vendor statement PDFs
 * @param open - Whether the modal is open
 * @param onClose - Function to call when the modal is closed
 * @returns
 */
// eslint-disable-next-line import/prefer-default-export
export const VendorStatementsUploadModal: React.FC<VendorStatementsUploadModalProps> = function ({
  open,
  onClose,
}) {
  const [files, setFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  /**
   * Handle file drop - only allow PDF files
   * @param acceptedFiles - The accepted files
   */
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const pdfFiles = acceptedFiles.filter((file) => file.type === 'application/pdf');
    if (pdfFiles.length !== acceptedFiles.length) {
      enqueueSnackbar('Only PDF files are allowed. Non-PDF files have been removed.', { variant: 'warning' });
    }
    setFiles((prev) => {
      const newFiles = [...prev, ...pdfFiles];
      // Limit the number of files to 10
      return newFiles.slice(0, 10);
    });
  }, [enqueueSnackbar]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'application/pdf',
    maxFiles: 10,
    multiple: true,
  });

  /**
   * Remove file from files state
   * @param index - The index of the file to remove
   */
  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * Upload files to Cloud Storage and initialize Job for each file
   */
  const uploadFiles = async () => {
    if (files.length === 0) return;

    setIsUploading(true);

    const uploadPromises = files.map(async (file) => initializeJob(file));

    const results = await Promise.all(uploadPromises);
    const successCount = results.filter((r) => r.success).length;
    const failureCount = results.filter((r) => !r.success).length;

    if (failureCount > 0) {
      enqueueSnackbar(
        `${successCount} files uploaded successfully, ${failureCount} files failed to upload.`,
        { variant: 'error' },
      );
    } else {
      enqueueSnackbar(`All ${successCount} files uploaded successfully!`, { variant: 'success' });
    }

    setFiles([]);
    setIsUploading(false);
    onClose();
  };

  /**
   * Handle close modal - only allow closing if not uploading
   */
  const handleClose = () => {
    if (!isUploading) {
      setFiles([]);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Upload Vendor Statements</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Upload up to 10 PDF files. Only PDF files are accepted.
          </Typography>
        </Box>

        {/* Dropzone */}
        <Box
          {...getRootProps()}
          sx={{
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'grey.300',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <input {...getInputProps()} />
          {isUploading ? (
            <Box>
              <CircularProgress size={40} />
              <Typography variant="body1" sx={{ mt: 2 }}>
                Uploading files...
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Dropzone title */}
              <Typography variant="h6" gutterBottom>
                {isDragActive ? 'Drop PDF files here' : 'Drag & drop PDF files here'}
              </Typography>
              {/* Dropzone subtitle */}
              <Typography variant="body2" color="text.secondary">
                or click to select files
              </Typography>
              {/* Dropzone caption */}
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Maximum 10 files, PDF only
              </Typography>
            </Box>
          )}
        </Box>

        {files.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Selected Files (
            {files.length}
            /10):
          </Typography>
          {/* List of uploaded files */}
          {files.map((file, index) => (
            <Box
              key={`${file.name}-${Math.random()}`}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 1,
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 1,
                mb: 1,
              }}
            >
              {/* File name */}
              <Typography variant="body2" noWrap sx={{ flex: 1, mr: 1 }}>
                {file.name}
              </Typography>
              {/* Remove file button */}
              <Button
                size="small"
                color="error"
                onClick={() => removeFile(index)}
                disabled={isUploading}
              >
                Remove
              </Button>
            </Box>
          ))}
        </Box>
        )}
      </DialogContent>
      <DialogActions>
        {/* Close modal button */}
        <Button onClick={handleClose} disabled={isUploading}>
          Cancel
        </Button>
        {/* Upload files button */}
        <Button
          onClick={uploadFiles}
          variant="contained"
          disabled={files.length === 0 || isUploading}
        >
          {isUploading ? 'Uploading...' : `Upload ${files.length} Files`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
